/**
 * Toast Component
 * Displays toast notifications
 */

import { motion, AnimatePresence } from 'framer-motion'
import { useEffect } from 'react'

const Toast = ({ toast, onRemove }) => {
  useEffect(() => {
    const timer = setTimeout(() => {
      onRemove(toast.id)
    }, 5000)

    return () => clearTimeout(timer)
  }, [toast.id, onRemove])

  const typeStyles = {
    success: 'bg-pitch-green text-stadium-black border-pitch-green',
    error: 'bg-red-500 text-white border-red-500',
    warning: 'bg-yellow-500 text-stadium-black border-yellow-500',
    info: 'bg-blue-500 text-white border-blue-500',
  }

  const isError = toast.type === 'error'
  return (
    <motion.div
      role={isError ? 'alert' : 'status'}
      aria-live={isError ? 'assertive' : 'polite'}
      initial={{ opacity: 0, y: -20, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, scale: 0.95, transition: { duration: 0.2 } }}
      className={`fixed top-20 right-4 z-50 min-w-[300px] max-w-md p-4 rounded-lg shadow-lg border-2 ${
        typeStyles[toast.type] || typeStyles.info
      }`}
    >
      <div className="flex items-start justify-between gap-4">
        <p className="font-semibold flex-1">{toast.message}</p>
        <button
          onClick={() => onRemove(toast.id)}
          className="text-current opacity-70 hover:opacity-100 transition-opacity"
          aria-label="Close toast"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    </motion.div>
  )
}

export const ToastContainer = ({ toasts, onRemove }) => {
  return (
    <div className="fixed top-20 right-4 z-50 space-y-2 pointer-events-none">
      <AnimatePresence>
        {toasts.map((toast) => (
          <div key={toast.id} className="pointer-events-auto">
            <Toast toast={toast} onRemove={onRemove} />
          </div>
        ))}
      </AnimatePresence>
    </div>
  )
}

export default Toast
